import { ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, inject, input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core'
import { AsyncPipe } from '@angular/common'
import { toSignal } from '@angular/core/rxjs-interop'
import { TranslatePipe } from '@ngx-translate/core'
import FindAndReplace from '@tiptap/extension-find-and-replace'
import { BehaviorSubject } from 'rxjs'
import { Call, isMinutesGenerationTargeted } from '../../../calls.service'
import { ProgressLoaderComponent } from '../../../common/progress-loader/progress-loader.component'
import { injectQueryClient } from '../../../query'
import { queryKeys } from '../../../query-keys'
import { LoggerService } from '../../../logger.service'
import { UsersService } from '../../../users.service'
import { EditorSettingsService } from '../../editor-settings.service'
import { deriveEditorDisplayState } from '../editor-display-state'
import { EditorRevisionsService, RestrictedItem } from '../editor-revisions.service'
import { FindReplacePanelComponent } from '../find-replace-panel/find-replace-panel.component'
import { MultiEditorFindReplaceTarget } from '../find-replace-panel/find-replace-target'
import { buildCaretUser } from '../extensions/unique-user-collaboration-caret'
import { MinutesGenerationProgressComponent } from '../minutes-generation-progress/minutes-generation-progress.component'
import { createScopeEditor, ScopeEditorHandle } from '../scope-editor'
import { STATELESS_REFETCH_CALL_MESSAGE_TYPE } from '../sse-sync-provider'

type LoadState = 'LOADING' | 'READY' | 'EMPTY' | 'ERROR'

/** How long the loader waits for every item to sync before showing the ones that have. */
const SECTION_SYNC_GRACE_MS = 10_000

/**
 * Number an item's scope editor as the full minutes do: the counter of its own list starts just below its position,
 * and the positions of its ancestors are prefixed, so item `[2, 1]` reads "2.1.".
 */
function applyOutlineNumber(section: HTMLElement, number: number[]): void {
  const ancestors = number.slice(0, -1)
  section.style.setProperty('--restricted-item-number-prefix', `"${ancestors.map((part) => `${part}.`).join('')}"`)
  section.style.setProperty('--restricted-item-counter-start', `${number[number.length - 1] - 1}`)
}

/**
 * The minutes view for a Restricted user. They never load the base document; instead each agenda item they are
 * assigned to is mounted as its own collaborative scope editor, so they only ever receive the content they may see.
 */
@Component({
  selector: 'app-restricted-minutes',
  // Maximised and zoomed as the minutes editor is, so the two lay the page out alike.
  host: { '[class.maximized]': 'isMaximized()', '[style.--editor-zoom]': 'isMaximized() ? zoomLevel() : 1' },
  standalone: true,
  imports: [AsyncPipe, FindReplacePanelComponent, MinutesGenerationProgressComponent, ProgressLoaderComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isGenerating()) {
      <app-minutes-generation-progress [call]="call()"></app-minutes-generation-progress>
    }
    @if ((findReplaceOpen$ | async) && loadState() === 'READY' && !isGenerating()) {
      <app-find-replace-panel
        class="find-replace-panel-host"
        [target]="findReplaceTarget"
        [canUserEdit]="true"
        (closed)="closeFindReplace()"
        (replaceRequested)="findReplaceTarget.replaceCurrent()"
        (replaceAllRequested)="findReplaceTarget.replaceAll()"
      ></app-find-replace-panel>
    }
    <div class="restricted-minutes" [hidden]="isGenerating()">
      @switch (loadState()) {
        @case ('LOADING') {
          <div class="centered-content">
            <app-progress-loader class="minutes-progress" [value]="progress()" loading></app-progress-loader>
            <span class="loading-text">{{ 'CALLS.MINUTES_LOADING_MESSAGE' | translate }}</span>
          </div>
        }
        @case ('EMPTY') {
          <div class="restricted-status">{{ 'CALLS.RESTRICTED_MINUTES_NO_ACCESS' | translate }}</div>
        }
        @case ('ERROR') {
          <div class="restricted-status">{{ 'CALLS.RESTRICTED_MINUTES_LOAD_FAILED' | translate }}</div>
        }
      }
      <div #container class="restricted-container" [hidden]="loadState() !== 'READY'"></div>
    </div>
  `,
  styleUrl: './restricted-minutes.component.scss',
})
export class RestrictedMinutesComponent implements OnInit, OnDestroy {
  readonly call = input.required<Call>()

  @ViewChild('container', { static: true }) container!: ElementRef<HTMLElement>
  @ViewChild(FindReplacePanelComponent) findReplacePanel?: FindReplacePanelComponent

  private readonly failure = signal<'EMPTY' | 'ERROR' | null>(null)
  private readonly itemCount = signal(0)
  private readonly syncedCount = signal(0)
  private readonly isSyncGraceOver = signal(false)
  private syncGraceTimer: ReturnType<typeof setTimeout> | null = null

  // Each item is shown once it has synced; the loader waits for all of them, but only for so long, so one item that
  // never syncs cannot keep the others hidden.
  readonly loadState = computed<LoadState>(() => {
    const failure = this.failure()
    if (failure !== null) {
      return failure
    }
    const isAllSynced = this.itemCount() > 0 && this.syncedCount() === this.itemCount()
    return isAllSynced || (this.isSyncGraceOver() && this.syncedCount() > 0) ? 'READY' : 'LOADING'
  })

  /** A full run regenerating the minutes covers them with its progress, exactly as it does for any other reader. */
  readonly isGenerating = computed(() => {
    const call = this.call()
    const displayState = deriveEditorDisplayState({
      isPostProcessingComplete: call.isPostProcessingComplete,
      isMinutesGenerationOngoing: call.isMinutesGenerationOngoing,
      isMinutesGenerationTargeted: isMinutesGenerationTargeted(call),
      minutesGenerationProgress: call.minutesGenerationProgress,
      hasEditorDocument: call.hasEditorDocument,
      isSpeechMissing: call.isSpeechMissing,
      editorConstructed: true,
      hasInitiallySynced: true,
    })
    return displayState === 'PROCESSING' || displayState === 'PROCESSING_WITH_PROGRESS'
  })

  readonly progress = computed(() => (this.itemCount() === 0 ? 0 : Math.round((this.syncedCount() / this.itemCount()) * 100)))

  private handles: ScopeEditorHandle[] = []

  /** Searches the items shown here — the only minutes a Restricted user has — as one list, in their order. */
  readonly findReplaceTarget = new MultiEditorFindReplaceTarget(() => this.handles.map(({ editor }, order) => ({ editor, anchorOf: () => order })))
  readonly findReplaceOpen$ = new BehaviorSubject<boolean>(false)
  private isDestroyed = false
  private caretUser = buildCaretUser(inject(UsersService).currentUser$.value)

  private editorRevisionsService = inject(EditorRevisionsService)
  private editorSettingsService = inject(EditorSettingsService)
  readonly isMaximized = toSignal(this.editorSettingsService.isEditorMaximized$, { initialValue: false })
  readonly zoomLevel = toSignal(this.editorSettingsService.zoomLevel$, { initialValue: this.editorSettingsService.zoomLevel })
  private queryClient = injectQueryClient()
  private logger = inject(LoggerService)

  async ngOnInit(): Promise<void> {
    try {
      const items = await this.editorRevisionsService.getRestrictedItems(this.call().id)
      // Left before the items arrived: mounting now would open scope editors nothing would ever close.
      if (this.isDestroyed) {
        return
      }
      if (items.length === 0) {
        this.failure.set('EMPTY')
        return
      }
      this.itemCount.set(items.length)
      items.forEach((item) => this.mountItem(item))
      this.syncGraceTimer = setTimeout(() => this.isSyncGraceOver.set(true), SECTION_SYNC_GRACE_MS)
    } catch (error) {
      this.logger.error('Failed to load restricted minutes sections', error)
      this.failure.set('ERROR')
    }
  }

  toggleFindReplace(): void {
    this.findReplaceOpen$.next(!this.findReplaceOpen$.value)
  }

  closeFindReplace(): void {
    this.findReplaceOpen$.next(false)
  }

  @HostListener('document:keydown', ['$event'])
  handleFindReplaceShortcut(event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey || event.key.toLowerCase() !== 'f') {
      return
    }
    if (!this.findReplaceOpen$.value && !this.handles.some(({ editor }) => editor.view.hasFocus())) {
      return
    }
    event.preventDefault()
    if (this.findReplaceOpen$.value) {
      this.findReplacePanel?.focusSearchInput()
      return
    }
    this.findReplaceOpen$.next(true)
  }

  ngOnDestroy(): void {
    this.isDestroyed = true
    this.findReplaceOpen$.complete()
    if (this.syncGraceTimer !== null) {
      clearTimeout(this.syncGraceTimer)
    }
    this.handles.forEach((handle) => handle.destroy())
    this.handles = []
  }

  private refetchCallOn(payload: string): void {
    if (payload === STATELESS_REFETCH_CALL_MESSAGE_TYPE) {
      void this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(this.call().id) })
    }
  }

  private mountItem(item: RestrictedItem): void {
    // The scope editor renders the item's own (styled) title and body, so no separate heading is added here.
    const section = window.document.createElement('section')
    section.classList.add('restricted-item')
    section.hidden = true
    if (item.number !== null) {
      applyOutlineNumber(section, item.number)
    }

    const host = window.document.createElement('div')
    section.appendChild(host)
    this.container.nativeElement.appendChild(section)

    this.handles.push(
      createScopeEditor({
        callId: this.call().id,
        templateItemId: item.templateItemId,
        element: host,
        caretUser: this.caretUser,
        editable: true,
        // A Restricted user edits the item's content only: no renaming, adding or removing agenda items.
        protectAgendaOutline: true,
        // Scope documents always run over polling, as the base document does whenever an item is restricted.
        forcePolling: true,
        onSynced: () => {
          section.hidden = false
          this.syncedCount.update((count) => count + 1)
        },
        onStatelessPayload: (payload) => this.refetchCallOn(payload),
        extensions: [FindAndReplace.configure({ injectCSS: false })],
      })
    )
  }
}
